import * as Sentry from "@sentry/remix";
import React from "react";
import { AnalyticsContext } from "~/contexts/analyticsContext";
import { AuthenticationContext } from "~/contexts/authenticationContext";
import { ReactRouterStubsContext } from "~/contexts/reactRouterStubsContext";
import { RequestHeadersContext } from "~/contexts/requestHeadersContext";
import {
  Event,
  Element,
  shouldBeSentToBackendSegmentSource,
} from "~/domain/analytics/event";
import { shouldTriggerAnalytics } from "~/domain/analytics/triggersAnalytics";
import { SegmentTrackFailed } from "~/domain/exception";
import { IdentityClaims } from "~/domain/tokens";

const fireEventOnBackendSource = (
  event: Event,
  isImpersonationSession: boolean | null,
) => {
  if (shouldBeSentToBackendSegmentSource(event.type)) {
    // Omit the await so we may fire and forget.
    void fetch("/api/track", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...event,
        properties: {
          ...event.properties,
          ...(isImpersonationSession !== null && {
            isImpersonationSession,
          }),
        },
      }),
    }).catch((rejectionReason) => {
      const hint = {
        extra: {
          event,
        },
      };

      if (rejectionReason instanceof Error) {
        Sentry.captureException(
          new SegmentTrackFailed(rejectionReason.message, rejectionReason),
          hint,
        );
      } else {
        if (rejectionReason.message) {
          Sentry.captureException(
            new SegmentTrackFailed(
              rejectionReason.message,
              new Error(rejectionReason.message),
            ),
            hint,
          );
        }
      }
    });
  }
};

// Pattern inspired by Segment React Example
// https://github.com/segmentio/react-example/blob/main/src/examples/analytics-package/useAnalytics.ts
//
// Additionally, the Remix documentation recommends using a `typeof document` check to
// ensure the code runs only in the browser.
// https://remix.run/docs/en/main/guides/constraints#document-guard
export const useAnalytics = () => {
  const authContext = React.useContext(AuthenticationContext);
  const requestHeaders = React.useContext(RequestHeadersContext);
  const stubs = React.useContext(ReactRouterStubsContext);

  const { location } = stubs;

  const { analytics, anonymousId } = React.useContext(AnalyticsContext);

  const context = React.useMemo(
    () => ({
      groupId: authContext.identity?.organizationId ?? null,
    }),
    [authContext.identity?.organizationId],
  );

  const isImpersonationSession =
    authContext.identity?.isImpersonationSession ?? null;

  const triggersAnalytics = shouldTriggerAnalytics({
    auth: authContext,
    userAgent: requestHeaders?.["user-agent"],
  });

  const track = React.useCallback(
    (event: Event) => {
      if (triggersAnalytics) {
        if (typeof document !== "undefined") {
          void analytics.track(
            event.type,
            {
              ...event.properties,
              ...(isImpersonationSession !== null && {
                is_impersonation: isImpersonationSession,
              }),
              page: location.pathname,
            },
            context,
          );
        }

        fireEventOnBackendSource(event, isImpersonationSession);
      }
    },
    [
      analytics,
      context,
      isImpersonationSession,
      location.pathname,
      triggersAnalytics,
    ],
  );

  const alias = React.useCallback(
    (userId: string) => {
      if (triggersAnalytics) {
        if (anonymousId) {
          if (typeof document !== "undefined") {
            void analytics.alias(userId, anonymousId);
          }

          const event: Event = {
            type: "alias",
            properties: {
              userId,
              anonymousId,
            },
          };

          fireEventOnBackendSource(event, isImpersonationSession);
        }
      }
    },
    [analytics, anonymousId, triggersAnalytics, isImpersonationSession],
  );

  const identify = React.useCallback(
    (identityClaims: IdentityClaims) => {
      if (triggersAnalytics) {
        if (identityClaims.userId) {
          const traits = {
            email: identityClaims.userEmail,
            domain: identityClaims.domain,
            role: identityClaims.role,
          };

          if (typeof document !== "undefined") {
            void analytics.identify(identityClaims.userId, traits);
          }

          const event: Event = {
            type: "identify",
            properties: {
              userId: identityClaims.userId,
              traits: {
                email: identityClaims.userEmail,
                domain: identityClaims.domain,
                role: identityClaims.role,
              },
            },
          };

          fireEventOnBackendSource(event, isImpersonationSession);
        }
      }
    },
    [analytics, triggersAnalytics, isImpersonationSession],
  );

  const trackLink = React.useCallback(
    (props: Omit<Element, "element_type" | "page">) => {
      track({
        type: "Element Clicked",
        properties: {
          ...props,
          element_type: "link",
          page: location.pathname,
        },
      });
    },
    [track, location.pathname],
  );

  const trackButton = React.useCallback(
    (props: Omit<Element, "element_type" | "page">) => {
      track({
        type: "Element Clicked",
        properties: {
          ...props,
          element_type: "button",
          page: location.pathname,
        },
      });
    },
    [track, location.pathname],
  );

  const trackDropdown = React.useCallback(
    (props: Omit<Element, "element_type" | "page">) => {
      track({
        type: "Dropdown Changed",
        properties: {
          ...props,
          element_type: "dropdown",
          page: location.pathname,
        },
      });
    },
    [track, location.pathname],
  );

  return {
    identify,
    alias,
    track,
    trackLink,
    trackButton,
    trackDropdown,
  };
};
